import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { supplierSettlementEntryTerm } from "./purchase-terminology.js";
import {
  isSupplierSettlementLedgerDirectory,
  isSupplierSettlementLedgerEntry,
  SupplierSettlementLedgerDirectoryEntrySchema,
  type SupplierSettlementLedgerEntry,
  SupplierSettlementLedgerEntrySchema,
  type SupplierSettlementLedgerSummary,
  SupplierSettlementLedgerSummarySchema,
} from "./supplier-settlement-ledger.js";

export const SUPPLIER_SETTLEMENT_LEDGER_EXPORT_FORMAT_VERSION =
  "SUPPLIER_SETTLEMENT_LEDGER_CSV_V1" as const;
export const SUPPLIER_SETTLEMENT_LEDGER_EXPORT_MAX_ENTRIES = 10_000 as const;

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });

export const SupplierSettlementLedgerExportSourceSchema = Type.Object(
  {
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    supplier: SupplierSettlementLedgerDirectoryEntrySchema,
    summary: SupplierSettlementLedgerSummarySchema,
    asOfGatewaySequence: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    entries: Type.Array(SupplierSettlementLedgerEntrySchema, {
      maxItems: SUPPLIER_SETTLEMENT_LEDGER_EXPORT_MAX_ENTRIES,
    }),
    generatedAt: TimestampSchema,
    evidence: Type.Literal("GATEWAY_AUTHORITY"),
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerExportSource = Static<
  typeof SupplierSettlementLedgerExportSourceSchema
>;

export const SupplierSettlementLedgerExportReadModelSchema = Type.Object(
  {
    formatVersion: Type.Literal(SUPPLIER_SETTLEMENT_LEDGER_EXPORT_FORMAT_VERSION),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    supplier: SupplierSettlementLedgerDirectoryEntrySchema,
    summary: SupplierSettlementLedgerSummarySchema,
    asOfGatewaySequence: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    entryCount: Type.Integer({
      minimum: 0,
      maximum: SUPPLIER_SETTLEMENT_LEDGER_EXPORT_MAX_ENTRIES,
    }),
    entries: Type.Array(SupplierSettlementLedgerEntrySchema, {
      maxItems: SUPPLIER_SETTLEMENT_LEDGER_EXPORT_MAX_ENTRIES,
    }),
    generatedAt: TimestampSchema,
    fileName: Type.String({ minLength: 1, maxLength: 128 }),
    csv: Type.String({ minLength: 1, maxLength: 16_777_216 }),
    contentSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    evidence: Type.Literal("GATEWAY_AUTHORITY"),
  },
  { additionalProperties: false },
);
export type SupplierSettlementLedgerExportReadModel = Static<
  typeof SupplierSettlementLedgerExportReadModelSchema
>;

function isCanonicalTimestamp(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function isCanonicalBusinessDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function summaryIsConsistent(summary: SupplierSettlementLedgerSummary): boolean {
  return (
    summary.receiptCount ===
    summary.openPayableReceiptCount +
      summary.supplierRefundDueReceiptCount +
      summary.settledReceiptCount
  );
}

function entryDeltasMatchSummary(
  summary: SupplierSettlementLedgerSummary,
  entries: readonly SupplierSettlementLedgerEntry[],
): boolean {
  let payableOutstandingFen = 0n;
  let supplierRefundDueFen = 0n;
  for (const entry of entries) {
    payableOutstandingFen += BigInt(entry.payableOutstandingDeltaFen);
    supplierRefundDueFen += BigInt(entry.supplierRefundDueDeltaFen);
  }
  return (
    payableOutstandingFen === BigInt(summary.payableOutstandingFen) &&
    supplierRefundDueFen === BigInt(summary.supplierRefundDueFen)
  );
}

export function isSupplierSettlementLedgerExportSource(
  value: unknown,
): value is SupplierSettlementLedgerExportSource {
  if (!Value.Check(SupplierSettlementLedgerExportSourceSchema, value)) return false;
  const source = value as SupplierSettlementLedgerExportSource;
  if (
    !isSupplierSettlementLedgerDirectory({
      suppliers: [source.supplier],
      generatedAt: source.generatedAt,
      evidence: "GATEWAY_LOCAL_DIRECTORY",
    }) ||
    !isCanonicalTimestamp(source.generatedAt) ||
    !isCanonicalTimestamp(source.supplier.lastKnownAt) ||
    source.supplier.lastKnownAt > source.generatedAt ||
    !summaryIsConsistent(source.summary) ||
    (source.summary.receiptCount === 0) !== (source.entries.length === 0) ||
    source.entries.filter((entry) => entry.kind === "PURCHASE_RECEIPT").length !==
      source.summary.receiptCount ||
    !entryDeltasMatchSummary(source.summary, source.entries)
  ) {
    return false;
  }
  return source.entries.every(
    (entry, index, entries) =>
      isSupplierSettlementLedgerEntry(entry) &&
      isCanonicalBusinessDate(entry.businessDate) &&
      isCanonicalTimestamp(entry.occurredAt) &&
      entry.occurredAt <= source.generatedAt &&
      entry.gatewaySequence <= source.asOfGatewaySequence &&
      (index === 0 || entry.gatewaySequence < (entries[index - 1]?.gatewaySequence ?? 0)),
  );
}

function csvText(value: string): string {
  const neutralized = /^[=+\-@\t\r]/u.test(value) ? `'${value}` : value;
  return `"${neutralized.replaceAll('"', '""')}"`;
}

function csvRow(values: readonly (string | number)[]): string {
  return values.map((value) => csvText(String(value))).join(",");
}

const DETAIL_HEADERS = [
  "往来类型",
  "业务编号",
  "业务日期",
  "发生时间",
  "本次金额（分）",
  "关联收货单",
  "关联采购单",
  "影响收货单数",
  "本店待付变动（分）",
  "本店待付变动前（分）",
  "本店待付变动后（分）",
  "供应商应退变动（分）",
  "供应商应退变动前（分）",
  "供应商应退变动后（分）",
  "业务说明",
  "核对建议",
] as const;

function entryCsvRow(entry: SupplierSettlementLedgerEntry): string {
  const term = supplierSettlementEntryTerm(entry.kind);
  return csvRow([
    term.label,
    entry.businessNumber,
    entry.businessDate,
    entry.occurredAt,
    entry.businessAmountFen,
    entry.receiptNumber ?? "合并付款，详见付款详情",
    entry.purchaseOrderNumber ?? "合并付款，详见付款详情",
    entry.relatedReceiptCount,
    entry.payableOutstandingDeltaFen,
    entry.payableOutstandingFenBefore,
    entry.payableOutstandingFenAfter,
    entry.supplierRefundDueDeltaFen,
    entry.supplierRefundDueFenBefore,
    entry.supplierRefundDueFenAfter,
    term.explanation,
    term.nextStep,
  ]);
}

export function buildSupplierSettlementLedgerCsv(
  source: SupplierSettlementLedgerExportSource,
): string {
  if (!isSupplierSettlementLedgerExportSource(source)) {
    throw new TypeError("供应商往来账无法安全生成完整导出文件");
  }
  const metadata = [
    csvRow(["供应商完整往来账", "只读导出；本店待付与供应商应退不会自动相抵"]),
    csvRow(["供应商编号", source.supplier.supplierCode]),
    csvRow(["供应商名称", source.supplier.supplierName]),
    csvRow(["数据生成时间", source.generatedAt]),
    csvRow(["本店待付（分）", source.summary.payableOutstandingFen]),
    csvRow(["供应商应退（分）", source.summary.supplierRefundDueFen]),
    csvRow(["收货单总数", source.summary.receiptCount]),
    csvRow(["仍待付收货单数", source.summary.openPayableReceiptCount]),
    csvRow(["仍应退收货单数", source.summary.supplierRefundDueReceiptCount]),
    csvRow(["已结清收货单数", source.summary.settledReceiptCount]),
    csvRow(["往来记录条数", source.entries.length]),
  ];
  const details = source.entries.map(entryCsvRow);
  return `\uFEFF${metadata.join("\r\n")}\r\n\r\n${csvRow(DETAIL_HEADERS)}\r\n${details.join("\r\n")}${details.length > 0 ? "\r\n" : ""}`;
}

export function supplierSettlementLedgerExportFileName(
  source: Pick<SupplierSettlementLedgerExportSource, "supplier" | "generatedAt">,
): string {
  if (!isCanonicalTimestamp(source.generatedAt)) {
    throw new RangeError("generatedAt must be a canonical UTC timestamp");
  }
  const compact = source.generatedAt.replace(/[-:]/gu, "");
  return `供应商往来账-${source.supplier.supplierCode}-${compact.slice(0, 8)}-${compact.slice(9, 13)}.csv`;
}

function exportSource(value: SupplierSettlementLedgerExportReadModel) {
  return {
    tenantId: value.tenantId,
    storeId: value.storeId,
    supplier: value.supplier,
    summary: value.summary,
    asOfGatewaySequence: value.asOfGatewaySequence,
    entries: value.entries,
    generatedAt: value.generatedAt,
    evidence: value.evidence,
  } satisfies SupplierSettlementLedgerExportSource;
}

export function isSupplierSettlementLedgerExportReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; supplierId: string },
): value is SupplierSettlementLedgerExportReadModel {
  if (!Value.Check(SupplierSettlementLedgerExportReadModelSchema, value)) return false;
  const report = value as SupplierSettlementLedgerExportReadModel;
  const source = exportSource(report);
  return (
    report.formatVersion === SUPPLIER_SETTLEMENT_LEDGER_EXPORT_FORMAT_VERSION &&
    report.entryCount === report.entries.length &&
    isSupplierSettlementLedgerExportSource(source) &&
    report.fileName === supplierSettlementLedgerExportFileName(source) &&
    report.csv === buildSupplierSettlementLedgerCsv(source) &&
    (expectedScope === undefined ||
      (report.tenantId === expectedScope.tenantId &&
        report.storeId === expectedScope.storeId &&
        report.supplier.supplierId === expectedScope.supplierId))
  );
}
